// frontend/src/ws.js

// Compute a robust default WS URL:
// - Prefer VITE_WS_URL when set (no surrounding whitespace)
// - Else, derive from VITE_API_URL by stripping trailing "/api" and switching http(s) -> ws(s), path=/ws
// - Else, fallback to same-origin (window.location), switching http(s) -> ws(s), path=/ws
function computeWsUrl() {
  const envUrlRaw = (import.meta?.env?.VITE_WS_URL || '').trim();
  if (envUrlRaw) return envUrlRaw;

  const apiRaw = (import.meta?.env?.VITE_API_URL || '').trim();
  try {
    if (apiRaw) {
      const u = new URL(apiRaw);
      // strip trailing /api for media/ws base like https://host/api -> wss://host/ws
      const isApiSuffix = u.pathname === '/api' || u.pathname === '/api/';
      const basePath = isApiSuffix ? '/' : u.pathname.endsWith('/') ? u.pathname : u.pathname + '/';
      const scheme = u.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${scheme}//${u.host}${basePath}ws`;
    }
  } catch {}

  // Fallback to same-origin
  try {
    const loc = window.location;
    const scheme = loc.protocol === 'https:' ? 'wss:' : 'ws:';
    // ensure trailing slash before ws
    const path = loc.pathname.endsWith('/') ? loc.pathname : loc.pathname + '/';
    return `${scheme}//${loc.host}${path}ws`;
  } catch {}

  // Last resort dev default
  return 'ws://localhost:5050';
}

const PRIMARY_WS_URL = computeWsUrl();

export function connectWebSocket(token, onMessage) {
  const candidates = [PRIMARY_WS_URL];
  // Fallback: same-origin /ws (in case API_URL points elsewhere)
  try {
    const loc = window.location;
    const scheme = loc.protocol === 'https:' ? 'wss:' : 'ws:';
    const p = loc.pathname.endsWith('/') ? loc.pathname : loc.pathname + '/';
    const so = `${scheme}//${loc.host}${p}ws`;
    if (!candidates.includes(so)) candidates.push(so);
  } catch {}
  // Fallback: explicit port 5050 on same host
  try {
    const loc = window.location;
    const scheme = loc.protocol === 'https:' ? 'wss:' : 'ws:';
    const h = loc.hostname;
    const portUrl = `${scheme}//${h}:5050`;
    if (!candidates.includes(portUrl)) candidates.push(portUrl);
  } catch {}

  let idx = 0;
  let ws = null;
  let opened = false;
  let reconnectDelay = 1000; // start 1s, max 30s
  let pingTimer = null;
  let reconnectTimer = null;
  let stopped = false;

  const connectNext = () => {
  if (stopped) return;
  const len = candidates.length;
  if (!len) return; // no targets (closed)
  const url = candidates[idx % len];
    const full = `${url}?token=${encodeURIComponent(token)}`;
    try {
      ws = new WebSocket(full);
    } catch (e) {
      scheduleReconnect();
      return;
    }
    opened = false;
    ws.onopen = () => {
      opened = true;
      reconnectDelay = 1000;
      // keepalive ping every 25s
      try { if (pingTimer) clearInterval(pingTimer); } catch {}
      pingTimer = setInterval(() => {
        try { ws && ws.readyState === WebSocket.OPEN && ws.send('ping'); } catch {}
      }, 25000);
      console.log('WebSocket connected', url);
    };
    ws.onmessage = (event) => {
      const dataStr = event.data;
      if (dataStr === 'pong') return; // keepalive
      try {
        const data = JSON.parse(dataStr);
        onMessage(data);
      } catch {}
    };
    ws.onerror = () => {
      if (!opened) {
        // try next candidate immediately
        try { ws.close(); } catch {}
      }
    };
    ws.onclose = () => {
      try { if (pingTimer) clearInterval(pingTimer); } catch {}
      console.log('WebSocket closed');
      scheduleReconnect();
    };
  };

  const scheduleReconnect = () => {
    if (stopped) return;
    idx++;
    const delay = Math.min(reconnectDelay, 30000);
    reconnectDelay = Math.min(reconnectDelay * 2, 30000);
    try { if (reconnectTimer) clearTimeout(reconnectTimer); } catch {}
    reconnectTimer = setTimeout(connectNext, delay);
  };

  connectNext();
  // Wrapper so externe Aufrufer immer das "aktuelle" Socket schließen können
  return {
    close: () => {
      stopped = true;
      try { if (pingTimer) clearInterval(pingTimer); } catch {}
      try { if (reconnectTimer) clearTimeout(reconnectTimer); } catch {}
      try { ws && ws.close(); } catch {}
    },
    get current() { return ws; }
  };
}
